import { useId } from "react";
import FeatureSpotlightCard from "./FeatureSpotlightCard";
import FeatureSpotlightPointer from "./FeatureSpotlightPointer";
import { AnchoredSurface, DockedSurface } from "./SpotlightSurface";
import { useFeatureSpotlight } from "./useFeatureSpotlight";
import { useSpotlightPointer } from "./useSpotlightPointer";

/**
 * FeatureSpotlightHost: as duas paradas de uma novidade, uma superfície por vez.
 *
 * 1. O CARTÃO: no máximo um por carregamento. No desktop fica à direita do item de menu da
 *    novidade, com uma seta apontando para ele, e acompanha o item se ele se mover ou mudar
 *    de tamanho. Abaixo do breakpoint `md` o menu lateral some, então o cartão vira um rodapé
 *    de largura total, sem seta.
 * 2. O PONTEIRO: depois de "Experimente", um anel no recurso dentro da tela (ver
 *    `useSpotlightPointer`). Enquanto o ponteiro está na tela, o cartão espera.
 *
 * Não bloqueia nada: sem sobreposição, sem prisão de foco, sem roubar o foco ao
 * aparecer, e Esc ou clique fora não fazem nada. Só "Fechar" e o botão da novidade
 * encerram o cartão (SPOT-05, SPOT-06).
 *
 * @param {{
 *   visibleNavKeys: string[],
 *   resolveAccountPath: (subPath: string) => string,
 *   canSeeTarget?: (target: object) => boolean,
 *   tourClientId?: string|null,
 *   enabled?: boolean,
 * }} props
 */
export default function FeatureSpotlightHost({
  visibleNavKeys,
  resolveAccountPath,
  canSeeTarget,
  tourClientId = null,
  enabled = true,
}) {
  const titleId = useId();
  const pointerTitleId = useId();
  const pointerState = useSpotlightPointer({ visibleNavKeys, canSeeTarget, tourClientId, enabled });
  const { spotlight, visible, anchorEl, isDesktop, dismiss, tryIt } = useFeatureSpotlight({
    visibleNavKeys,
    resolveAccountPath,
    canSeeTarget,
    tourClientId,
    suppressed: pointerState.visible,
    enabled,
  });

  if (!enabled) return null;

  const card = visible && (
    <FeatureSpotlightCard
      title={spotlight.title}
      body={spotlight.body}
      mediaUrl={spotlight.mediaUrl}
      mediaType={spotlight.mediaType}
      ctaLabel={spotlight.ctaLabel}
      titleId={titleId}
      onDismiss={dismiss}
      onTry={tryIt}
    />
  );

  return (
    <>
      {/* Anuncia o cartão sem mover o foco: uma região viva já montada recebe o texto. */}
      <div role="status" aria-live="polite" className="sr-only">
        {pointerState.visible ? `Novidade: ${pointerState.pointer.title}` : visible ? `Novidade: ${spotlight.title}` : ""}
      </div>
      {pointerState.visible && (
        <FeatureSpotlightPointer
          element={pointerState.element}
          title={pointerState.pointer.title}
          body={pointerState.guide?.text ?? pointerState.pointer.body}
          stepLabel={pointerState.stepLabel}
          insideModal={pointerState.insideModal}
          titleId={pointerTitleId}
          onAcknowledge={pointerState.acknowledge}
        />
      )}
      {visible &&
        (isDesktop ? (
          <AnchoredSurface anchorEl={anchorEl} titleId={titleId}>
            {card}
          </AnchoredSurface>
        ) : (
          <DockedSurface titleId={titleId}>{card}</DockedSurface>
        ))}
    </>
  );
}
